import _ from 'lodash'
import { useEffect } from 'react'
import { fork, select, put, call } from 'redux-saga/effects'
import { v1 as generateUUID } from 'uuid'

import { createESubmissionPolicy } from 'core/service-wrapper/partner-api/handle-esub/policy'
import { createInsuredInformationHandler } from 'core/service-wrapper/partner-api/handler/insured-information'
import { createProductBumnanReadyService } from 'core/service-wrapper/partner-api/handler/product-bumnan-ready'
import { createProductCommonService } from 'core/service-wrapper/partner-api/handler/product-common'
import { createProductCommonBenefitService } from 'core/service-wrapper/partner-api/handler/product-common-benefit'
import { createProductIHealthyUltraService } from 'core/service-wrapper/partner-api/handler/product-ihealthy-ultra'
import { createProductInvestmentService } from 'core/service-wrapper/partner-api/handler/product-investment'
import { createProductLifeReadyService } from 'core/service-wrapper/partner-api/handler/product-life-ready'
import { createProductMRTACoveragePlanService } from 'core/service-wrapper/partner-api/handler/product-mrta-coverage'
import { createProductPACoveragePlanService } from 'core/service-wrapper/partner-api/handler/product-pa-coverage'
import { createProductTermLifeService } from 'core/service-wrapper/partner-api/handler/product-term-life'
import { createProductLifeProtectPlusService } from 'core/service-wrapper/partner-api/handler/product-life-protect-plus'
import { partnerApiRequest } from 'core/service-wrapper/partner-api/handler-request/service-request'
import { getAppConfig } from 'deploy-env/app-config'
import { getCurrentApp } from 'e-submission/apps/selectors'
import { requestWrapper } from 'e-submission/domain/data'
import { getQuickQuoteBasicOccupationFactor } from 'e-submission/domain/data-model/form/selectors'
import { default as esubStore } from 'e-submission/store'
import { toDataURL } from 'e-submission/utils'
import { getDistributorGroups, getDistributorGroupsStatus } from 'identity/selectors'
import { getOccupationsFailed, getOccupationsSuccess } from 'quick-quote/actions'
import { PATH } from 'quick-quote/constants/path'
import { getToggles } from 'quick-quote/feature-toggles'
import { SELECT_GENDER, UPDATE_BIRTHDATE, UPDATE_PAYER_BIRTHDATE } from 'quick-quote/insured-information/actions'
import { getModelFactorsStatus } from 'quick-quote/product-common/coverage-plan/selectors'
import {
  getDisplayProducts,
  getDisplayProductsStatus,
  getSelectedDisplayProduct,
} from 'quick-quote/product-selection/selectors'
import { getFnaConfiguration, getViewConfiguration } from 'quick-quote/selectors'
import { getModelFactors } from 'quick-quote/selectors/model-factors'
import { getPartnerCurrentSelectedProduct, isFetchingPolicy } from 'quick-quote/selectors/partner-selector'

export function createPartnerApiCache() {
  return {
    showRiderUnsellable: true,
    riderBenefits: [],
    route_state: {
      is_quick_quote: false,
      insured_information: false,
      coverage_plan: false,
      benefit_illlustration: false,
      fund_allocation: false,
    },
    flag: {
      skipGeneratePolicy: false,
    },
  }
}

function* checkApplicationDataReady() {
  const products = yield select(getDisplayProducts)
  const productsStatus = yield select(getDisplayProductsStatus)
  const modelFactors = yield select(getModelFactors)
  const modelFactorsStatus = yield select(getModelFactorsStatus)
  const distributorGroups = yield select(getDistributorGroups)
  const distributorGroupsStatus = yield select(getDistributorGroupsStatus)
  const viewConfiguration = yield select(getViewConfiguration)
  const fnaConfiguration = yield select(getFnaConfiguration)

  const statuses = [productsStatus, modelFactorsStatus, distributorGroupsStatus]
  const items = [products, modelFactors, distributorGroups]

  // prettier-ignore
  return [(
    statuses.includes('error') ||
    items.some((item) => item.length === 0) ||
    Object.keys(fnaConfiguration).length === 0 ||
    fnaConfiguration.reasonToSelectProduct.length === 0 ||
    Object.keys(viewConfiguration).length === 0 ||
    viewConfiguration.productList.length === 0
  ), items.every(item => item.length)]
}

function isOrdinaryForceLoginProduct(displayProduct) {
  const { ENABLE_FORCE_LOGIN_ORDINARY_PRODUCT } = getToggles()
  return ENABLE_FORCE_LOGIN_ORDINARY_PRODUCT && displayProduct.ordinaryProductForceLogin
}

/**
 *
 * @param cache {ReturnType<import('./service').createPartnerApiCache>}
 * @param productCommonService {ReturnType<import('core/service-wrapper/partner-api/handler/product-common').createProductCommonService>}
 */
function subscribeRouteChange(cache, productCommonService) {
  return function*(action) {
    const pathname = action.payload
    const fetching_policy = yield select(isFetchingPolicy)

    cache.route_state.is_quick_quote = pathname.startsWith(PATH.QUICK_QUOTE)
    cache.route_state.insured_information = pathname.endsWith(PATH.INSURED_INFORMATION)
    cache.route_state.coverage_plan = pathname.endsWith(PATH.COVERAGE_PLAN)
    cache.route_state.benefit_illlustration = pathname.endsWith(PATH.BENEFIT_ILLUSTRATION)
    cache.route_state.fund_allocation =
      pathname.endsWith(PATH.FUND_ALLOCATION) || pathname.endsWith(PATH.LSTU_FUND_ALLOCATION)

    const shouldReCalculate = cache.route_state.insured_information || cache.route_state.coverage_plan
    cache.flag.skipGeneratePolicy = !shouldReCalculate
    if (shouldReCalculate && fetching_policy === false) {
      const currentSelectedProduct = yield select(getPartnerCurrentSelectedProduct)
      const comingDisplayProduct = yield select(getSelectedDisplayProduct)
      const usePartner = !_.isNil(comingDisplayProduct.useLegacy) && !comingDisplayProduct.useLegacy

      if (
        usePartner &&
        !_.isEmpty(currentSelectedProduct.packageCategory) &&
        !_.isEmpty(currentSelectedProduct.packageCode)
      ) {
        if (cache.route_state.insured_information) {
          yield fork(
            productCommonService.getProductPackageBySelectedProduct,
            currentSelectedProduct.packageCategory,
            currentSelectedProduct.packageCode,
            comingDisplayProduct,
            false
          )
        } else {
          yield fork(productCommonService.calculateWithValidation, { calculateByPremium: false })
        }
      }
    }
  }
}

/**
 *
 * @param legacy {Awaited<ReturnType<import('core/service-wrapper/legacy/service').createLegacyService>>}
 */
export async function createPartnerApiService(legacy) {
  const proxyHandler = {
    set(obj, property, value) {
      obj[property] = value
      return true
    },
  }
  const cache = new Proxy(createPartnerApiCache(), proxyHandler)
  let productCommonService = new Proxy({}, proxyHandler)

  const insuredInformationHandler = createInsuredInformationHandler(productCommonService)
  productCommonService = Object.assign(
    productCommonService,
    createProductCommonService(cache, insuredInformationHandler)
  )
  const productCommonBenefit = createProductCommonBenefitService()
  const productLifeReadyService = createProductLifeReadyService(productCommonService)
  const productIHealthyUltraService = createProductIHealthyUltraService(productCommonService)
  const productTermLifeCoverageService = createProductTermLifeService()
  const productBumnanReadyService = createProductBumnanReadyService()
  const productInvestmentService = createProductInvestmentService(productCommonService)
  const productMRTACoverage = createProductMRTACoveragePlanService(productCommonService)
  const productPACoverage = createProductPACoveragePlanService(productCommonService)
  const productLifeProtectPlusService = createProductLifeProtectPlusService(productCommonService)

  // e-submission
  const eSubmissionPolicy = createESubmissionPolicy()

  // function* initial() {
  //   const dataDump = dataDumpHandler.loadDataDumpForPartnerApi()
  //
  //   cache.riderBenefits = dataDump.riderBenefits
  //   cache.loanHealthCheck = dataDump.loanHealthCheck
  //   cache.loanFinancialCheck = dataDump.loanFinancialCheck
  //
  //   yield put(getDistributorGroupsSuccess(dataDump.distributorGroups))
  //   yield put(getModelFactorsSuccess(dataDump.modelFactors))
  //   yield put(getOccupationsSuccess({}))
  //
  //   // view-configuration
  //   yield call(productCommonService.getProductCategories)
  //   // fna-configuration
  //   yield call(productCommonService.getFnaConfiguration)
  //   // rpq-questionnaire
  //   yield fork(productInvestmentService.loadRPQQuestionnaire)
  //
  //   const insuredAge = yield select(getAge)
  //   const insuredBirthdate = moment()
  //     .add(-insuredAge.value, 'year')
  //     .format('DD/MM/YYYY')
  //   yield fork(insuredInformationHandler.insured.onEditBirthdate, { birthdate: insuredBirthdate, forceUpdate: true })
  // }

  return {
    isPartnerApi: true,
    checkApplicationDataReady: checkApplicationDataReady,
    isOrdinaryForceLoginProduct,
    subscribeRouteChange: subscribeRouteChange(cache, productCommonService),
    dump: {
      loadProducts: function*() {
        return yield call(productCommonService.getProductCategories)
      },
      loadFNAConfiguration: function*() {
        yield call(productCommonService.getFnaConfiguration)
      },
      loadQuestionnaire: function*() {
        yield fork(productInvestmentService.loadRPQQuestionnaire)
      },
      updateRidersBenefits: (riderBenefits) => {
        cache.riderBenefits = riderBenefits
      },
      loadOccupations: function*() {
        try {
          const occupations = yield call(partnerApiRequest.getOccupations)
          yield put(getOccupationsSuccess(occupations))
        } catch (error) {
          yield put(getOccupationsFailed(error))
        }
      },
    },
    insured: {
      onEditBirthdate: insuredInformationHandler.insured.onEditBirthdate,
      onSelectNatureOfDuty: insuredInformationHandler.insured.onSelectNatureOfDuty,
      onSelectOtherNatureOfDuty: insuredInformationHandler.insured.onSelectOtherNatureOfDuty,
    },
    payer: {
      onEditBirthdate: insuredInformationHandler.payer.onEditBirthdate,
      onSelectNatureOfDuty: insuredInformationHandler.payer.onSelectNatureOfDuty,
      onSelectOtherNatureOfDuty: insuredInformationHandler.payer.onSelectOtherNatureOfDuty,
      onUpdatePayerNeed: insuredInformationHandler.payer.onUpdatePayerNeed,
    },

    getBasicPlan: productCommonService.getBasicPlan,
    getRiskiestOccupation: (displayProductQuery, natureOfDuty) => {
      const state = esubStore.getState()
      const currentApp = getCurrentApp(state)
      const occupationFactor = getQuickQuoteBasicOccupationFactor(currentApp)
      return occupationFactor
    },
    getRiskiestOccupationFactorForRiders: async (displayProductQuery, natureOfDutyCodes, riders) => {
      // TODO should change to use occupationFactor from api?
      // const state = esubStore.getState()
      // const currentApp = getCurrentApp(state)
      // const selectedRiders = getSelectedRiders(currentApp)
      //
      // const riderOccupationFactors = {}
      // for (const rider of selectedRiders) {
      //   if (rider.isSelected && rider.isSelectable) {
      //     riderOccupationFactors[rider.code] = rider.occupationFactor
      //   }
      // }
      //
      // return riderOccupationFactors

      if (riders.length === 0) return {}
      const riderCodes = riders.map((rider) => rider.code)
      const occupationFactorsForRiders = (await legacy.getDBWrapper().getOccupationFactorsForRiders()).filter(
        (occ) => natureOfDutyCodes.includes(occ.natureOfDutyCode) && riderCodes.includes(occ.riderCode)
      )

      const riderOccupationFactors = {}
      for (const rider of riders) {
        const isExists = occupationFactorsForRiders.some((occ) => occ.riderCode === rider.code)
        if (!isExists || rider.occupationFactor === -1) riderOccupationFactors[rider.code] = ''
        else riderOccupationFactors[rider.code] = rider.occupationFactor
      }
      return riderOccupationFactors
    },
    renderGraph: (basicPlanCode, className) => {
      return (() => {
        const elementId = generateUUID()

        useEffect(() => {
          loadImage()
        }, [])

        async function loadImage() {
          const url = new URL(
            `/partner-api/proposal/image/${basicPlanCode}`,
            getAppConfig().ADVISORZONE_MIDDLEWARE
          ).toString()
          try {
            const result = await toDataURL(url)

            const image = document.createElement('img')
            image.src = result
            image.style.width = '100%'
            image.style.height = 'auto'

            document.getElementById(elementId).appendChild(image)
          } catch (_) {
            // ignored
          }
        }

        return <div className={className} id={elementId}></div>
      })()
    },
    products: {
      COMMON: {
        updateProductPackage: function*(action) {
          const currentSelectedProduct = yield select(getPartnerCurrentSelectedProduct)
          const accept_actions = [SELECT_GENDER, UPDATE_BIRTHDATE, UPDATE_PAYER_BIRTHDATE]
          if (
            !accept_actions.includes(action.type) ||
            _.isEmpty(currentSelectedProduct.packageCategory) ||
            _.isEmpty(currentSelectedProduct.packageCode)
          ) {
            return
          }

          const comingDisplayProduct = yield select(getSelectedDisplayProduct)
          yield productCommonService.getProductPackageBySelectedProduct(
            currentSelectedProduct.packageCategory,
            currentSelectedProduct.packageCode,
            comingDisplayProduct
          )
        },
        onSelectProduct: productCommonService.onSelectProduct,
        onSelectModelFactor: productCommonService.onSelectModelFactor,
        onCalculateFromSumAssuredChange: productCommonService.onCalculateFromSumAssuredChange,
        onCalculateFromPremiumChange: productCommonService.onCalculateFromPremiumChange,
        onToggleRider: productCommonService.onToggleRider,
        onEditRiderSelectPlan: productCommonService.onEditRiderSelectPlan,
        onCalculateFromRiderSumAssuredChange: productCommonService.onCalculateFromRiderSumAssuredChange,
        onCalculateFromRiderPremiumChange: productCommonService.onCalculateFromRiderPremiumChange,
        onSelectPensionType: productCommonService.onSelectPensionType,
        onUpdateSelectedBasicPlan: productCommonService.onUpdateSelectedBasicPlan,
        // CommonPDFFunc
        prepareBIProps: productCommonBenefit.prepareBIProps,
        generatePDFToBase64: productCommonBenefit.generatePDFToBase64,
        generateRaAndRpqForm: productCommonBenefit.generateRaAndRpqForm,
      },
      BUMNAN_READY: {
        getSelectableProductBumnanReady: productBumnanReadyService.getSelectableProductBumnanReady,
        switchBumnanReadyProductByAge: () => {},
      },
      HEALTH: {},
      HEALTH_TOPPING: {},
      I_HEALTHY_ULTRA: {
        getBasicPlanOptions: productIHealthyUltraService.getBasicPlanOptions,
        getSelectedDisplayProductCodeIHealthyUltra:
          productIHealthyUltraService.getSelectedDisplayProductCodeIHealthyUltra,
      },
      INVESTMENT: {
        getFundLimit: (_displayProduct) => ({
          maxTotalAllocation: 100,
          minFundAllocation: 5,
        }),
        getSellableFunds: productInvestmentService.getSellableFunds,
        onSelectedModalFactor: productInvestmentService.onSelectedModalFactor,
        onEditSumAssured: productInvestmentService.onEditSumAssured,
        onEditRegularTopUp: productInvestmentService.onEditRegularTopUp,
        onEditLumpSum: productInvestmentService.onEditLumpSum,
        onEditRegularPremium: productInvestmentService.onEditRegularPremium,
      },
      LIFE_READY: {
        onUpdateSelectedLifeReady: productLifeReadyService.onSelectProduct('LIFE_READY'),
        onUpdateSelectedLifeTreasure: productLifeReadyService.onSelectProduct('LIFE_TREASURE'),
        onUpdateSelectedCancerX5: productLifeReadyService.onSelectProduct('CANCERX5'),
        onUpdateSelectedKoomverCover: productLifeReadyService.onSelectProduct('KOOMVER_COVER'),
        onUpdateSelectKoomverCompensate: productLifeReadyService.onSelectProduct('KOOMVER_COMPENSATE'),
      },
      MRTA: {
        selectProductMRTA: productMRTACoverage.selectProductMRTA,
        validateLoanPeriodChange: productMRTACoverage.validateLoanPeriodChange,
        validateCoveragePeriodChange: productMRTACoverage.validateCoveragePeriodChange,
        resetInterestRate: productMRTACoverage.resetInterestRate,
        onEditLoanAmount: productMRTACoverage.onEditLoanAmount,
        validateInterestRateErrors: productMRTACoverage.validateInterestRateErrors,
      },
      PA: {
        getAvailableOptionPlanPARider: productPACoverage.getAvailableOptionPlanPARider,
        resolveRiderOptionForPlan: productPACoverage.resolveRiderOptionForPlan,
      },
      TERM_LIFE: {
        getSelectableProductUltimateGrowth: productTermLifeCoverageService.getSelectableProductUltimateGrowth,
      },
      LIFE_PROTECT_PLUS: {
        onUpdateSwitchPlanLifeProtectPlus: productLifeProtectPlusService.onUpdateSwitchPlanLifeProtectPlus(
          'LIFE_PROTECT_PLUS'
        ),
        onUpdateSelectedBoosterPlan: productLifeProtectPlusService.onSelectBoosterPlan('LIFE_PROTECT_PLUS'),
      },
    },
    e_submission: {
      policy: {
        generatePdfProps: eSubmissionPolicy.generatePdfProps,
      },
    },
  }
}

export const getQuestionareSuitability = (user, payload) => {
  return requestWrapper.postRequest(`suitability/get-questionare-suitability`, user, payload).then(({ data }) => data)
}

export const submitSuitabilityAnswer = (user, payload) => {
  return requestWrapper.postRequest(`suitability/submit-suitability-answer`, user, payload).then(({ data }) => data)
}
